import { Check, LocalOffer, MoreVert } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { OrderItem, PromotionApplied } from '../../../apiTypes';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import currency from '../../../utils/currency';
import EditMods from './EditMods';
import OrderItemRow from './OrderItem';

const canEdit = (item: OrderItem): boolean => item.status !== 'ready';

type Props = {
  items: OrderItem[];
  preview?: boolean;
  onEditItem?: (item: OrderItem) => void;
  onRevoveItem?: (item: OrderItem) => void;
  canRemove: boolean;
  canEditWight: boolean;
  promotionsApplied: PromotionApplied[];
  fiscalized?: boolean;
};

const OrderItemsList = ({
  items,
  preview = false,
  onEditItem,
  onRevoveItem,
  canRemove,
  canEditWight,
  promotionsApplied,
  fiscalized,
}: Props) => {
  const { lang } = useLocale();
  const { dishesMap, worker } = useCafe();
  const { cafe } = useAdmin();

  const [editItem, setEditItem] = useState<OrderItem | null>(null);

  const itemsByOrder = useMemo(() => {
    const itemsMap: Record<OrderItem['queue'], OrderItem[]> = { '1': [], '2': [], '3': [] };
    items.forEach((item) => {
      const key = item.queue || '1';
      itemsMap[key] = [...itemsMap[key], item];
    });
    return itemsMap;
  }, [items]);

  const hasQuene = useMemo(() => itemsByOrder['2'].length > 0 || itemsByOrder['3'].length > 0, [itemsByOrder]);

  const handleEditItem = onEditItem
    ? (item: OrderItem) => {
        onEditItem(item);
        setEditItem(null);
      }
    : undefined;

  const handleMarkAsComplete = (item: OrderItem) => {
    onEditItem && onEditItem({ ...item, status: 'complete' });
  };

  const handleItemClick = (item: OrderItem) => {
    if (canEdit(item) && worker?.credentials.create_and_edit_own_orders) {
      setEditItem(item);
      return;
    }
    if (item.status === 'ready') {
      handleMarkAsComplete(item);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {Object.entries(itemsByOrder).map(([queue, items]) => (
        <Box key={queue} sx={{ mt: 1 }}>
          {hasQuene && items.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ ml: 1 }} variant="subtitle2" color={(t) => t.palette.text.secondary}>
                {`${t.queue[lang]} ${queue}`}
              </Typography>
            </Box>
          )}

          {items.map((item) => (
            <OrderItemRow
              key={item.id}
              item={item}
              dishesMap={dishesMap}
              onClick={handleItemClick}
              noWrap={preview}
              rightContent={
                preview ? undefined : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {(item.status === 'ready' ||
                      (cafe?.orderStatusesSetting === 'short' && item.status !== 'complete')) && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsComplete(item);
                        }}
                        disabled={!worker?.credentials.create_and_edit_own_orders}
                      >
                        <Check />
                      </IconButton>
                    )}

                    {canEdit(item) && (
                      <div>
                        <IconButton disabled={!worker?.credentials.create_and_edit_own_orders}>
                          <MoreVert />
                        </IconButton>
                      </div>
                    )}
                  </Box>
                )
              }
            />
          ))}
        </Box>
      ))}

      {promotionsApplied.map((promo, index) => (
        <Box
          key={promo.name + index}
          sx={{
            display: 'flex',
            gap: 1,
            py: 0.5,
            px: 1,
            alignItems: 'center',
            borderRadius: 1,
          }}
        >
          <LocalOffer fontSize="small" color="secondary" />
          <Typography color={(t) => t.palette.text.secondary}>{promo.name}:</Typography>
          <Typography color={(t) => t.palette.text.secondary}>{`-${currency(
            promo.discountSum,
            cafe?.currency,
            true
          )}`}</Typography>
        </Box>
      ))}

      {editItem && handleEditItem && onRevoveItem && (
        <EditMods
          open
          item={editItem}
          onChange={handleEditItem}
          onRemove={onRevoveItem}
          onClose={() => setEditItem(null)}
          canRemove={canRemove}
          canEditWight={canEditWight}
          fiscalized={fiscalized}
        />
      )}
    </Box>
  );
};

export default OrderItemsList;
